import axios from "axios";
import { createAuthClient } from "@comai-portal/auth-ui";
import type { AuthClient } from "@comai-portal/auth-ui";
import type {
  Manager,
  AuthTokens,
  LoginManagerRequest,
  RegisterManagerRequest,
} from "@comai-portal/auth-ui";

/**
 * portal-app API client (CO 032-001, §6; CO 048 Phase 1).
 *
 * The auth surface is no longer hand-rolled here — it comes from the shared
 * `@comai-portal/auth-ui` client (`createAuthClient`), the single source of the
 * `/managers/*` calls, the bearer-token request interceptor, and the 401 → refresh
 * retry interceptor. This file binds that shared client to portal's backend
 * baseURL and re-exports the same `api` surface portal's pages already consume,
 * so no caller changes. Google methods (`loginWithGoogle`/`registerWithGoogle`/
 * `verifyGoogleToken`) now exist here for the first time, sourced from the shared
 * client — never redefined.
 */
// CO 199 — resolve the API base SAME-ORIGIN by default.
//
// The gateway serves this SPA on multiple hosts (comos-federation.com,
// mcp.comos-federation.com, comos-portal.com, mcp.comos-gateway.com). A baked
// absolute base (the old `https://mcp.comos-gateway.com`) made the SPA call a
// DIFFERENT host than the one it loads from → the browser blocks the
// credentialed cross-origin request (CORS), `/managers/me` fails, AuthProvider
// clears the token, and the user bounces back to /login after an otherwise
// successful OAuth login. (This was the prod GitHub-login bounce.)
//
// Fix: when VITE_API_BASE_URL is not explicitly set, use "" — an empty axios
// baseURL resolves every request relative to the page origin, so the SPA always
// talks to the SAME host that served it, on every domain, with no CORS. An
// explicit VITE_API_BASE_URL still wins (e.g. local dev pointing at :4500 or
// :8080). Only fall back to the localhost default when there is no window
// (SSR / unit tests), where a relative URL has no origin to resolve against.
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window !== "undefined" ? "" : "http://localhost:4500");

const authClient: AuthClient = createAuthClient({ baseURL: API_BASE_URL });

// Re-export the canonical auth types so portal's existing imports keep resolving.
export type {
  Manager,
  AuthTokens,
  LoginManagerRequest,
  RegisterManagerRequest,
};

/**
 * portal's `api` surface, backed by the shared auth client. Method shapes are
 * unchanged from the previous hand-rolled client; `logout` stays portal-local
 * (it owns the post-logout redirect to "/").
 */
export const api = {
  registerManager: authClient.registerManager,
  loginManager: authClient.loginManager,
  loginWithGoogle: authClient.loginWithGoogle,
  registerWithGoogle: authClient.registerWithGoogle,
  verifyGoogleToken: authClient.verifyGoogleToken,
  refreshToken: authClient.refreshToken,
  verifyEmail: authClient.verifyEmail,
  requestPasswordReset: authClient.requestPasswordReset,
  resetPassword: authClient.resetPassword,
  getCurrentManager: authClient.getCurrentManager,

  logout: (): void => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/";
  },
};

/** The shared auth client, for the AuthProvider to mount. */
export { authClient };

/**
 * Public blog API (no auth). The blog's source of truth is the federation
 * gateway's `/api/blog`, backed by the marketing platform's blog lane
 * (`marketing_posts`) on the tenant the gateway names in BLOG_TENANT_ID — the
 * same store the `marketing_post_*` tools write (CO 536). There is no bundled
 * fallback: an unreachable API is rendered as such by the pages.
 */
/** CO 540 — membership in an ordered folder (series). */
export interface ApiBlogSeriesRef {
  key: string;
  title: string;
  part: number;
}

export interface ApiBlogPost {
  _id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  author: { name: string; role: string };
  category: string;
  tags: string[];
  status: "draft" | "published";
  views: number;
  readingTime: number;
  publishedAt?: string;
  featuredImage?: string;
  // CO 540 — optional relationships; absent on rows that predate them.
  series?: ApiBlogSeriesRef | null;
  related?: string[];
  createdAt: string;
  updatedAt: string;
}

/** CO 540 — one folder of the catalog `/api/blog/series` serves. */
export interface ApiBlogSeriesFolder {
  key: string;
  title: string;
  count: number;
  posts: ApiBlogPost[];
}

export interface ApiBlogCatalog {
  series: ApiBlogSeriesFolder[];
  standalone: ApiBlogPost[];
}

// Public blog client — no credentials needed for the public `/api/blog` endpoints.
const publicBlogClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

export const blogAPI = {
  getPosts: async (params?: {
    category?: string;
    tag?: string;
    search?: string;
    /** CO 540 — only posts in this series (by key). */
    series?: string;
    /** CO 540 — sort_order (default) | newest | oldest | most_read | reading_order. */
    sort?: string;
    limit?: number;
    skip?: number;
  }): Promise<{ posts: ApiBlogPost[]; total: number }> => {
    const response = await publicBlogClient.get("/api/blog", { params });
    return response.data;
  },

  // CO 540 — the grouped projection: series folders (parts in reading order)
  // then standalone posts. The same projection the marketing_post_catalog tool
  // serves an agent, so the page and the tool cannot disagree.
  getCatalog: async (): Promise<ApiBlogCatalog> => {
    const response = await publicBlogClient.get("/api/blog/series");
    return { series: response.data.series ?? [], standalone: response.data.standalone ?? [] };
  },

  getCategories: async (): Promise<Array<{ name: string; count: number }>> => {
    const response = await publicBlogClient.get("/api/blog/categories");
    return response.data.categories ?? [];
  },

  // `null` means the post does not exist (404); any other failure throws so the
  // page can tell "not found" from "the blog is unreachable".
  getPost: async (slug: string): Promise<ApiBlogPost | null> => {
    try {
      const response = await publicBlogClient.get(`/api/blog/${slug}`);
      return response.data.post;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },
};

/**
 * Public pricing config (CO 068). The two flat margins + Stripe-at-cost rates,
 * read by the pricing page. comMargin/stripeMargin are FRACTIONS (0.02 = 2%);
 * 0 means "not set" → the page shows the form, never a fabricated number.
 */
export interface PublicFeeConfig {
  comMargin: number; // fraction; 0 = not set
  stripeMargin: number; // fraction; 0 = not set
  stripeDomesticPercent: number; // Stripe at cost, passed through (e.g. 2.9)
  stripeDomesticFlat: number; // e.g. 0.30
  stripePassthrough: boolean;
}

const publicPricingClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

export const pricingAPI = {
  /** Returns the live fee config, or null if the endpoint is unreachable. */
  getConfig: async (): Promise<PublicFeeConfig | null> => {
    try {
      const response = await publicPricingClient.get("/pricing-config");
      return response.data?.data ?? null;
    } catch {
      return null;
    }
  },
};

/** Extract a human-readable message from an unknown axios/Error value. */
export function extractApiError(error: unknown, fallback: string): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as Record<string, unknown>).response === "object"
  ) {
    const data = (error as { response?: { data?: { error?: string } } })
      .response?.data?.error;
    if (data) return data;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

export default authClient.http;
